import { format } from 'date-fns-tz';
import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import { company } from '~/company/Company';
import { compareLeadTime } from '~/components/ModalSlotsSelectDateCD/components';
import { useDateLang } from '~/hooks/useDateLang';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import { cartStore, checkoutStore, mainStore, orderStore } from '~/stores';
import { formatPriceWithCurrency } from '~/utils/formaters';

import { AvailableOffers } from '../AvailableOffers';

import type { SlotItemProps } from './interfaces';
import styles from './SlotItem.module.scss';

const SlotItem = ({
  slot,
  priceWithoutDelivery,
  onSelect,
  isSelected,
}: SlotItemProps) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();

  const {
    schedule_slot_id,
    delivery_min_time,
    delivery_max_time,
    delivery_price,
    order_amount_threshold,
    free_delivery_threshold,
  } = slot;

  const currentDelivery = checkoutStore.slotSelectionModalStore.selectedSlot;

  const currentItems = cartStore.selectedCartItems.filter(({ sku }) =>
    currentDelivery?.itemsIds.includes(sku),
  );

  const unavailableProducts = checkoutStore.isDeliverTogether
    ? currentItems.filter((cartItem) => {
        const { count, sellableWarehouses, backorderLeadTime } = cartItem;
        const currentWHStock = Number(
          sellableWarehouses[`${orderStore.etaCalculation?.warehouse.code}`] ||
            '0',
        );

        if (currentWHStock >= count && orderStore.isExpressAvailableNow) {
          return false;
        }
        return compareLeadTime(delivery_min_time, backorderLeadTime);
      })
    : [];

  const getSlotPrice = () => {
    const deliveryPrice = +mainStore.convertPenceToPounds(delivery_price);
    const deliveryTreshold = +mainStore.convertPenceToPounds(
      free_delivery_threshold,
    );
    const orderAmountThreshold = +mainStore.convertPenceToPounds(
      order_amount_threshold ?? 0,
    );

    const isFreeDelivery =
      deliveryPrice === 0 || deliveryTreshold <= priceWithoutDelivery;

    return {
      minOrderAmount: orderAmountThreshold > 0 ? orderAmountThreshold : null,
      deliveryFee: isFreeDelivery ? null : deliveryPrice,
      isFreeDelivery,
    };
  };

  const { minOrderAmount, deliveryFee, isFreeDelivery } = getSlotPrice();

  const handleSelect = () => {
    onSelect(schedule_slot_id, unavailableProducts.length > 0);
  };

  const availableProductCount =
    currentItems.length - unavailableProducts.length;
  const itemLabelDeclension =
    availableProductCount > 1
      ? t('slotSelection:items', { count: availableProductCount })
      : t('slotSelection:item');

  return (
    <div
      className={classes([styles.root, isSelected && styles.selected])}
      role="button"
      tabIndex={-1}
      onClick={handleSelect}
    >
      <div className={styles.inner}>
        <div className={styles.timeWr}>
          <span className={styles.time}>
            {format(new Date(delivery_min_time * 1000), timeFormat, {
              timeZone: company.config.timeZone,
              locale,
            })}
            {' - '}
            {format(new Date(delivery_max_time * 1000), timeFormat, {
              timeZone: company.config.timeZone,
              locale,
            })}
          </span>
          {minOrderAmount && (
            <span className={styles.minOrderAmount}>
              {t('checkout:minimumOrderValue', {
                price: formatPriceWithCurrency(minOrderAmount),
              })}
            </span>
          )}
          {unavailableProducts.length > 0 && (
            <span className={styles.unavailableLabel}>
              {t('slotSelection:availableProductsForSlot', {
                expression: itemLabelDeclension,
              })}
            </span>
          )}
        </div>

        <div
          className={classes([
            styles.deliveryFee,
            isFreeDelivery && styles.free,
          ])}
        >
          {isFreeDelivery
            ? t('checkout:deliveryFree')
            : formatPriceWithCurrency(deliveryFee!)}
        </div>
      </div>
      <AvailableOffers
        currentItems={currentItems}
        unavailableProducts={unavailableProducts}
      />
    </div>
  );
};

export default observer(SlotItem);
